<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :info-cards="pageInfoCards"
  >
    <template #actions>
      <md-button class="md-primary md-round" @click="goToVendor">
        <md-icon>business</md-icon>
        Assign to Vendor
      </md-button>
      <md-button class="md-info md-round" @click="goToUpload">
        <md-icon>cloud_upload</md-icon>
        Bulk Upload
      </md-button>
      <md-button class="md-success md-round" @click="goToAdd">
        <md-icon>add</md-icon>
        Add Labourer
      </md-button>
    </template>

    <template #content>
      <!-- Filters Bar (station + vendor + status) -->
      <div class="filters-bar">
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-30 md-small-size-100">
            <select-input
                v-model="selectedStation"
                track-by="id"
                name="name"
                placeholder="All Stations"
                :options="stations"
                :allow-empty="true"
                :reduce-to-value="true"
                @option:selected="onStationChange"
            />
          </div>

          <div class="md-layout-item md-size-30 md-small-size-100">
            <select-input
                v-model="selectedVendor"
                track-by="id"
                name="name"
                placeholder="All Vendors"
                :options="vendors"
                :allow-empty="true"
                :reduce-to-value="true"
                :disabled="!selectedStation"
                @option:selected="onVendorChange"
            />
          </div>

          <div class="md-layout-item md-size-40 md-small-size-100"></div>
        </div>

        <!-- Status filter chips -->
        <div class="status-chips" v-if="statuses && statuses.length">

          <button
              v-for="status in statuses"
              :key="status.key"
              type="button"
              class="status-chip"
              :class="{ active: filteredStatus === status.key }"
              @click="labourerStatusesFilter(status.key)"
          >
            <md-icon>{{ status.icon }}</md-icon>
            <span class="chip-label">{{ status.title }}</span>
            <span class="chip-count">{{ status.count }}</span>
          </button>
        </div>
      </div>

      <!-- DataTable (handles search, pagination, per-page, state) -->
      <table-data
          ref="table"
          title="Labourers"
          :columns="columns"
          :data="table"
          :total="total"
          :loading="isLoading"
          loading-text="Loading labourers..."
          :searchable="true"
          search-placeholder="Search by name, phone, or NIDA..."
          storage-key="labourers-list"
          empty-message="No labourers found"
          @fetch="onTableFetch"
      >
        <template #toolbar-right>
          <md-button class="md-primary md-round" @click="goToAdd">
            <md-icon>add</md-icon>
            Add Labourer
          </md-button>
        </template>

        <!-- Name column -->
        <template #cell-name="{ item }">
          <div class="name-cell">
            <clickable-link
                :id="item.id"
                resource="Labourer"
                :text="getFullName(item)"
                :stop-propagation="true"
            />
            <small class="nida-number">{{ item.nida_number || 'No NIDA' }}</small>
          </div>
        </template>

        <!-- Phone -->
        <template #cell-phone_number="{ item }">
          <span v-if="item.phone_number">{{ item.phone_number }}</span>
          <span v-else class="text-muted">N/A</span>
        </template>

        <!-- Takeon count -->
        <template #cell-takeon_count="{ item }">
          <chip type="primary">{{ item.takeon_count || 0 }}</chip>
        </template>

        <!-- Port -->
        <template #cell-port="{ item }">
          <span v-if="item.port">{{ item.port.name }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <!-- Department -->
        <template #cell-department="{ item }">
          <span v-if="item.department">{{ item.department.name }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <!-- Skills -->
        <template #cell-skills="{ item }">
          <chip
              type="primary"
              icon="psychology"
              :count="item.skills.length"
              :clickable="true"
              tag="button"
              @click="showSkills(item)"
          />
        </template>

        <!-- Vendor -->
        <template #cell-vendor="{ item }">
          <div class="vendor-cell">
            <badge :type="getVendorBadgeType(item)">
              {{ getVendorName(item) }}
            </badge>
            <small v-if="item.vendor_labourer_number">
              #{{ item.vendor_labourer_number }}
            </small>
          </div>
        </template>

        <!-- Status -->
          <template #cell-status="{ item }">
            <chip :type="getStatusChipType(item)">
              {{ getStatusName(item) }}
            </chip>
          </template>

        <!-- Actions -->
        <template #actions="{ item }">
          <md-button class="md-icon-button md-primary" @click="goToView(item.id)" title="View">
            <md-icon>visibility</md-icon>
          </md-button>
          <md-button class="md-icon-button md-info" @click="goToEdit(item.id)" title="Edit">
            <md-icon>edit</md-icon>
          </md-button>
          <md-button class="md-icon-button md-danger" @click="destroy(item)" title="Delete">
            <md-icon>delete</md-icon>
          </md-button>
        </template>

        <template #empty-actions>
          <md-button class="md-primary md-round" @click="goToAdd">
            <md-icon>add</md-icon>
            Add First Labourer
          </md-button>
        </template>
      </table-data>
    </template>
  </page-layout>
</template>

<script>
import PageLayout from '@/components/Layouts/PageLayout.vue';
import TableData from '@/components/DataTable/TableData.vue';
import ClickableLink from '@/components/ClickableLink.vue';
import Badge from '@/components/Badge.vue';
import SelectInput from '@/components/Inputs/SelectInput.vue';
import PageMixin from '@/mixins/page-mixin';
import Swal from 'sweetalert2';

export default {
  name: 'ListLabourerPage',
  components: {
    PageLayout: PageLayout,
    TableData: TableData,
    ClickableLink: ClickableLink,
    Badge: Badge,
    SelectInput: SelectInput
  },
  mixins: [PageMixin],
  data() {
    return {
      table: [],
      total: 0,
      isLoading: false,
      statuses: [],
      stations: [],
      vendors: [],
      selectedStation: null,
      selectedVendor: null,
      filteredStatus: null,
      tableState: {
        page: 1,
        perPage: 10,
        search: ''
      },
      columns: [
        { field: 'name', label: 'Full Name' },
        { field: 'phone_number', label: 'Phone' },
        { field: 'takeon_count', label: 'Take-ons', align: 'text-right' },
        { field: 'port', label: 'Port' },
        { field: 'department', label: 'Department' },
        { field: 'skills', label: 'Skills', align: 'center' },
        { field: 'vendor', label: 'Vendor' },
        { field: 'status', label: 'Status' }
      ]
    };
  },
  computed: {
    pageTitle() {
      return 'Labourers';
    },
    pageBreadcrumb() {
      return [
        { label: 'Dashboard', path: '/' },
        { label: 'Labourers' }
      ];
    },
    pageInfoCards() {
      var total = this.total || 0;
      var onPage = this.table.length;
      var activeStatus = null;

      if (this.statuses && this.statuses.length) {
        var self = this;
        var match = this.statuses.find(function (s) {
          return s.key === self.filteredStatus;
        });
        if (match) activeStatus = match;
      }

      return [
        {
          icon: 'people',
          label: 'Total Labourers',
          value: total,
          color: 'primary'
        },
        {
          icon: 'pageview',
          label: 'On This Page',
          value: onPage,
          color: 'info'
        },
        {
          icon: activeStatus ? activeStatus.icon : 'filter_list',
          label: activeStatus ? activeStatus.title : 'No Filter',
          value: activeStatus ? activeStatus.count : '—',
          color: 'warning'
        },
        {
          icon: 'business',
          label: 'Stations',
          value: this.stations.length,
          color: 'success'
        }
      ];
    }
  },
  created() {
    this.loadStations();
  },
  methods: {
    // ============ TABLE STATE ============
    onTableFetch(state) {
      this.tableState = state;
      this.fetchData();
    },

    // ============ DATA FETCH ============
    async fetchData() {
      this.isLoading = true;

      try {
        var filter = this.buildFilter();
        var self = this;

        var listParams = {
          include: 'port,vendor,skills,department',
          filter: filter,
          page: {
            number: this.tableState.page,
            size: this.tableState.perPage
          }
        };

        var statusParams = {
          filter: filter,
          page: { size: 1000 }
        };

        // Fetch list + status counts in parallel
        var results = await Promise.all([
          this.$store.dispatch('labourers/list', listParams).then(function () {
            return {
              list: self.$store.getters['labourers/list'] || [],
              total: self.$store.getters['labourers/listTotal'] || 0
            };
          }),
          this.$store.dispatch('labourers/statusStatistics', statusParams).then(function () {
            return self.$store.getters['labourers/statistics'] || [];
          }).catch(function () {
            return [];
          })
        ]);

        this.table = results[0].list;
        this.total = results[0].total;
        this.statuses = results[1];

      } catch (error) {
        console.error('Error fetching labourers:', error);
        this.$store.dispatch('alerts/error', 'Failed to load labourers');
        this.table = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    buildFilter() {
      var filter = {};

      if (this.tableState.search && this.tableState.search.trim()) {
        filter.query_key = this.tableState.search.trim();
      }

      if (this.filteredStatus) {
        filter.status = this.filteredStatus;
      }

      if (this.selectedStation) {
        filter.station = this.selectedStation;
      }

      if (this.selectedVendor) {
        filter.vendor = this.selectedVendor;
      }

      return filter;
    },

    // ============ STATION / VENDOR ============
    onStationChange(option) {
      // Reset vendor when station changes
      this.selectedVendor = null;
      this.vendors = [];

      if (this.selectedStation) {
        this.loadVendorsForStation(this.selectedStation);
      }

      // Reset table to page 1
      this.resetTableAndFetch();
    },

    onVendorChange() {
      this.resetTableAndFetch();
    },

    labourerStatusesFilter(statusKey) {
      this.filteredStatus = this.filteredStatus === statusKey ? null : statusKey;
      this.resetTableAndFetch();
    },

    resetTableAndFetch() {
      if (this.$refs.table) {
        this.$refs.table.currentPage = 1;
        this.$refs.table.persistState();
        this.$refs.table.emitFetch();
      } else {
        this.fetchData();
      }
    },

    // ============ LOADERS ============
    async loadStations() {
      var cached = sessionStorage.getItem('stations_cache');

      if (cached) {
        try {
          var parsed = JSON.parse(cached);
          if (Date.now() - parsed.timestamp < 5 * 60 * 1000) {
            this.stations = parsed.data;
            return;
          }
        } catch (e) {
          console.warn('Cache parse error', e);
        }
      }

      try {
        await this.$store.dispatch('stations/list', {
          include: 'tariffs,tariffs.vendor',
          page: { size: 1000 }
        });

        this.stations = this.$store.getters['stations/list'] || [];

        sessionStorage.setItem('stations_cache', JSON.stringify({
          data: this.stations,
          timestamp: Date.now()
        }));
      } catch (error) {
        console.error('Error loading stations:', error);
        this.stations = [];
      }
    },

    loadVendorsForStation(stationId) {
      if (!stationId) {
        this.vendors = [];
        return;
      }

      var cacheKey = 'vendors_cache_' + stationId;
      var cached = sessionStorage.getItem(cacheKey);

      if (cached) {
        try {
          var parsed = JSON.parse(cached);
          if (Date.now() - parsed.timestamp < 5 * 60 * 1000) {
            this.vendors = parsed.data;
            return;
          }
        } catch (e) {
          console.warn('Cache parse error', e);
        }
      }

      var station = this.stations.find(function (s) {
        return s.id === stationId;
      });

      if (!station || !station.tariffs) {
        this.vendors = [];
        return;
      }

      var uniqueVendors = {};
      station.tariffs.forEach(function (tariff) {
        if (tariff.vendor) {
          uniqueVendors[tariff.vendor.id] = tariff.vendor;
        }
      });

      this.vendors = Object.values(uniqueVendors);

      sessionStorage.setItem(cacheKey, JSON.stringify({
        data: this.vendors,
        timestamp: Date.now()
      }));
    },

    // ============ DELETE ============
    async destroy(item) {
      var self = this;

      var confirmation = await Swal.fire({
        title: 'Delete this labourer?',
        text: 'This action cannot be undone!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (!confirmation.value) return;

      try {
        await this.$store.dispatch('labourers/destroy', item.id);
        await this.$store.dispatch('alerts/success', 'Labourer deleted successfully.');

        // If we just deleted the last row on a non-first page, go back a page
        if (this.table.length === 1 && this.tableState.page > 1 && this.$refs.table) {
          this.$refs.table.currentPage = this.tableState.page - 1;
          this.$refs.table.persistState();
        }

        this.resetTableAndFetch();
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors && error.response.data.errors[0]) {
          this.$store.dispatch('alerts/error', error.response.data.errors[0].title);
        } else {
          this.$store.dispatch('alerts/error', 'Failed to delete labourer');
        }
      }
    },

    // ============ HELPERS ============
    getFullName(item) {
      var parts = [];
      if (item.first_name) parts.push(item.first_name);
      if (item.middle_name) parts.push(item.middle_name);
      if (item.last_name) parts.push(item.last_name);
      return parts.length ? parts.join(' ') : 'N/A';
    },

    getVendorName(item) {
      if (item.vendor && item.vendor[0]) {
        return item.vendor[0].short_name || item.vendor[0].name || 'Vendor';
      }
      return 'No Vendor';
    },

    getVendorBadgeType(item) {
      if (item.vendor && item.vendor[0]) {
        return 'rose';
      }
      return 'default';
    },

    getStatusName(item) {
      if (item.status && item.status.name) {
        return item.status.name;
      }
      return 'Unknown';
    },

    getStatusBadgeType(item) {
      var statusName = this.getStatusName(item);
      var badges = {
        'Active': 'success',
        'Suspended': 'warning',
        'Terminated': 'danger',
        'Inactive': 'danger'
      };
      return badges[statusName] || 'info';
    },

    showSkills(item) {
      var self = this;
      var skills = item.skills || [];
      var skillList = skills.map(function (s, i) {
        return (i + 1) + '. ' + (s.name || 'Unknown');
      }).join('<br>');

      Swal.fire({
        title: 'Skills for ' + (item.first_name || 'Labourer'),
        html: skills.length ? '<div style="text-align:left">' + skillList + '</div>' : 'No skills available.',
        icon: 'info',
        confirmButtonText: 'Close'
      });
    },

    // ============ NAVIGATION ============
    goToAdd() {
      this.$router.push({ name: 'Add Labourer' });
    },

    goToUpload() {
      this.$router.push({ name: 'Labourers Upload' });
    },

    goToVendor() {
      this.$router.push({ name: 'List Labourer Terminated' });
    },

    goToEdit(id) {
      this.$router.push({ name: 'Edit Labourer', params: { id: id } });
    },

    goToView(id) {
      this.$router.push({ name: 'View Labourer', params: { id: id } });
    },
    getStatusChipType(item) {
      var name = this.getStatusName(item);
      var map = {
        'Active': 'success',
        'Suspended': 'warning',
        'Terminated': 'danger',
        'Inactive': 'secondary'
      };
      return map[name] || 'info';
    }
  }
};
</script>

<style lang="scss" scoped>
.filters-bar {
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 20px;
}

/* ============ STATUS CHIPS ============ */
.status-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  transition: all 0.2s ease;
  outline: none;

  &:hover {
    border-color: #667eea;
    background: #f8f9ff;
  }

  &.active {
    background: #667eea;
    border-color: #667eea;
    color: white;

    .chip-count {
      background: rgba(255, 255, 255, 0.25);
      color: white;
    }

    .md-icon {
      color: white;
    }
  }

  .md-icon {
    font-size: 18px !important;
    width: 18px !important;
    height: 18px !important;
    color: #667eea;
  }

  .chip-label {
    font-weight: 600;
  }

  .chip-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 8px;
    background: #f0f0f5;
    color: #555;
    border-radius: 11px;
    font-size: 11px;
    font-weight: 700;
  }
}

/* ============ CELLS ============ */
.name-cell {
  display: flex;
  flex-direction: column;

  .nida-number {
    font-size: 11px;
    color: #999;
    margin-top: 2px;
  }
}


.vendor-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;

  small {
    font-size: 11px;
    color: #999;
  }
}

.clickable-count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: #f0f0f5;
  border: none;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #667eea;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e0e4ff;
  }

  .md-icon {
    font-size: 14px !important;
    width: 14px !important;
    height: 14px !important;
  }
}

.text-muted {
  color: #999;
  font-style: italic;
}

@media (max-width: 768px) {
  .status-chips {
    justify-content: center;
  }

  .status-chip {
    font-size: 12px;
    padding: 6px 10px;
  }
}
</style>